/**
 * A sheet of paper, on screen and on paper. Shared by the two documents a
 * shareholder can take away — the kvittering and the aksjeeierbevis.
 *
 * ## Why it prints rather than downloading a PDF
 *
 * Bjarte, 2 Sep 2026: *nedlastingen kan ta brukeren til en print document — så
 * vi ikke ferdig rendrer PDFer.* The browser already has a PDF writer behind
 * Ctrl-P, and it produces a better file than a bundled renderer would: real
 * text, real fonts, selectable and searchable. So this is a page, and Print is
 * the whole of the export.
 *
 * ## How the page gets printed without the app around it
 *
 * The sheet is portalled to `document.body`, making it a sibling of `#root`
 * rather than a node inside it. `@media print` then hides `#root` outright,
 * which is one rule and cannot be defeated by a parent's overflow, transform or
 * stacking context — the things that make printing a nested overlay fail. It is
 * also why this is not a `Modal`: a dialog is furniture, and furniture that has
 * to be hidden from a printer one class at a time is furniture that will one day
 * print.
 *
 * ## Two languages, one of them the default
 *
 * Both documents open in **Norwegian** and offer English behind a flag in the
 * top right corner — Bjarte, 3 Sep 2026. Norwegian is the default because that
 * is the language these are filed in here; English exists because the register
 * has holders in more than one country and somebody has to be able to read what
 * they are filing.
 *
 * The flag is on the sheet rather than in the button bar because it belongs to
 * the document, not to the app — and it is the one thing on the paper that
 * print removes, for the same reason.
 *
 * The **interface stays English** in both states: Close, Print, and the buttons
 * that opened this. The language switch is about the document.
 */
import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { type DocLang, type MissingField } from '@/types/shareDocuments'
import './PrintSheet.css'

/** A labelled fact. Absent reads as a dash, never as a blank the eye skips. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="kvitt__field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}

const GAP_WORDS: Record<DocLang, Record<MissingField, string>> = {
  nb: {
    companyName: 'selskapets navn',
    companyOrgNumber: 'organisasjonsnummer',
    parValue: 'pålydende per aksje',
    holderAddress: 'aksjonærens adresse',
    holderId: 'aksjonærens fødsels- eller organisasjonsnummer',
    price: 'tegningskurs',
    paidDate: 'innbetalingsdato',
    registered: 'melding til Foretaksregisteret',
  },
  en: {
    companyName: 'the company name',
    companyOrgNumber: 'the organisation number',
    parValue: 'the par value per share',
    holderAddress: 'the shareholder’s address',
    holderId: 'the shareholder’s national identity or organisation number',
    price: 'the subscription price',
    paidDate: 'the payment date',
    registered: 'the filing with the Register of Business Enterprises',
  },
}

const GAP_LEDE: Record<DocLang, (list: string) => string> = {
  nb: (list) =>
    `Ikke registrert: ${list}. Dokumentet er utstedt med disse feltene åpne — et dokument som gjetter på dem er verre enn ett som viser hullet.`,
  en: (list) =>
    `Not on file: ${list}. This document is issued with those fields left open — one that guesses at them is worse than one that shows the gap.`,
}

/**
 * The facts nothing on file could answer, printed at the top of the document.
 *
 * The same device as the board minutes: a document that invents an organisation
 * number looks like a record, and one that shows the hole is merely incomplete.
 */
export function Gaps({ missing, lang }: { missing: MissingField[]; lang: DocLang }) {
  if (missing.length === 0) return null
  return <p className="kvitt__gap">{GAP_LEDE[lang](missing.map((m) => GAP_WORDS[lang][m]).join(', '))}</p>
}

/**
 * The flag of the language you would switch *to*, which is the one worth
 * clicking. Emoji flags are not an option: Windows renders a regional indicator
 * pair as the two letters, so a 🇬🇧 would reach most of this register's readers
 * as "GB".
 */
function Flag({ to }: { to: DocLang }) {
  return to === 'en' ? (
    <svg viewBox="0 0 60 30" aria-hidden="true" focusable="false">
      <clipPath id="printsheet-union-clip">
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path
        d="M0,0 L60,30 M60,0 L0,30"
        clipPath="url(#printsheet-union-clip)"
        stroke="#c8102e"
        strokeWidth="4"
      />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#c8102e" strokeWidth="6" />
    </svg>
  ) : (
    <svg viewBox="0 0 22 16" aria-hidden="true" focusable="false">
      <rect width="22" height="16" fill="#ba0c2f" />
      <path d="M6,0 h4 v16 h-4 z M0,6 h22 v4 h-22 z" fill="#fff" />
      <path d="M7,0 h2 v16 h-2 z M0,7 h22 v2 h-22 z" fill="#00205b" />
    </svg>
  )
}

interface Props {
  /** Screen-reader name for the sheet; not printed. */
  label: string
  lang: DocLang
  onLang: (next: DocLang) => void
  onClose: () => void
  children: ReactNode
}

export function PrintSheet({ label, lang, onLang, onClose, children }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const other: DocLang = lang === 'nb' ? 'en' : 'nb'

  return createPortal(
    <div className="kvitt__scrim" role="dialog" aria-modal="true" aria-label={label}>
      {/* Screen-only chrome, in English like the rest of the interface. */}
      <div className="kvitt__bar">
        <button className="btn btn--ghost" onClick={onClose}>
          Close
        </button>
        <button className="btn btn--primary" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
      </div>

      <article className="kvitt__paper">
        {/* Top right of the document, and removed from the printed copy: a
            language switch on a filed certificate would read as part of it. */}
        <button
          className="kvitt__lang"
          onClick={() => onLang(other)}
          title={other === 'en' ? 'Read in English' : 'Les på norsk'}
          aria-label={other === 'en' ? 'Read in English' : 'Read in Norwegian'}
        >
          <Flag to={other} />
        </button>
        {children}
      </article>
    </div>,
    document.body,
  )
}
